import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { authedLayoutRoute } from './authed.js';
import {
  uploadImport,
  commitImport,
  cancelImport,
  downloadTemplate,
  downloadErrorReport,
  employeeImportColumns,
  type ImportCountry,
  type ImportSummary,
} from '../api/employeeImports.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

/**
 * /employees/import — three-step employee CSV bulk import (PR ⑧).
 * Upload (dry-run) → Review (valid/errors/warnings + commit) → result. One
 * route component holding the step state internally. RBAC enforced by the API.
 */
export const employeeImportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/employees/import',
  component: EmployeeImportPage,
});

const COUNTRIES: ImportCountry[] = ['NG', 'GH', 'CD'];

function EmployeeImportPage(): JSX.Element {
  const navigate = useNavigate();
  const [country, setCountry] = useState<ImportCountry>('NG');
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [skipErrors, setSkipErrors] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: () => uploadImport(file!, country),
    onSuccess: (s) => { setErr(null); setSummary(s); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Upload failed.'),
  });
  const commit = useMutation({
    mutationFn: () => commitImport(summary!.id, skipErrors),
    onSuccess: async () => { await navigate({ to: '/employees' }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Commit failed; no rows were imported.'),
  });
  const cancel = useMutation({
    mutationFn: () => cancelImport(summary!.id),
    onSuccess: () => { setSummary(null); setSkipErrors(false); setErr(null); },
  });

  const canCommit = summary !== null && (summary.errorRowCount === 0 || skipErrors);

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <nav className="text-xs text-gray-500">
        <Link to="/employees" className="hover:underline">Employees</Link> / Bulk import
      </nav>
      <h1 className="font-serif text-3xl">Bulk import employees</h1>

      {err ? <Alert variant="error">{err}</Alert> : null}

      {summary === null ? (
        // ---- Step 1: Upload ---------------------------------------------------
        <form
          className="rounded border border-gray-900 p-5 space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (file) upload.mutate(); }}
        >
          <fieldset className="space-y-2">
            <legend className="text-sm text-gray-300">Country (one per file)</legend>
            <p className="text-xs text-gray-500">The template and the required columns differ by country — pick the country these employees are paid in first.</p>
            <div className="flex gap-3">
              {COUNTRIES.map((c) => (
                <label key={c} className="flex items-center gap-1 text-sm text-gray-300">
                  {/* Switching country invalidates a file picked for another country
                      (currency + NG state_code differ), so clear it — the country is
                      load-bearing, not cosmetic. */}
                  <input type="radio" name="country" value={c} checked={country === c} onChange={() => { setCountry(c); setFile(null); }} />
                  {c}
                </label>
              ))}
            </div>
          </fieldset>

          <ol className="space-y-4">
            {/* Step 1 — the template as the PRIMARY starting point, not a buried link. */}
            <li className="rounded border border-gray-900 p-4 space-y-2">
              <div className="text-sm text-gray-300"><span className="font-medium">Step 1.</span> Download the {country} template and fill it in.</div>
              <button type="button" onClick={() => void downloadTemplate(country)} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200">
                ⬇ Download {country} template
              </button>
              <details className="text-xs text-gray-400">
                <summary className="cursor-pointer text-gray-300">Expected columns ({country})</summary>
                <table className="mt-2 w-full">
                  <thead className="text-left text-gray-500"><tr><th className="py-1 pr-3">Column</th><th className="py-1 pr-3">Required</th><th className="py-1">Notes</th></tr></thead>
                  <tbody>
                    {employeeImportColumns(country).map((c) => (
                      <tr key={c.name} className="border-t border-gray-900"><td className="py-1 pr-3 font-mono text-gray-300">{c.name}</td><td className="py-1 pr-3">{c.required}</td><td className="py-1">{c.note}</td></tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </li>

            {/* Step 2 — upload the filled CSV. */}
            <li className="rounded border border-gray-900 p-4 space-y-2">
              <div className="text-sm text-gray-300"><span className="font-medium">Step 2.</span> Upload your filled {country} CSV for validation.</div>
              <input
                type="file"
                accept=".csv"
                aria-label="CSV file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block text-sm text-gray-300"
              />
              <button
                type="submit"
                disabled={!file || upload.isPending}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
              >
                {upload.isPending ? 'Validating…' : 'Upload & validate'}
              </button>
            </li>
          </ol>
        </form>
      ) : (
        // ---- Step 2: Review ---------------------------------------------------
        <div className="space-y-5">
          <div className="flex gap-2 text-xs">
            <span className="rounded px-2 py-1 border border-green-800 text-green-400">{summary.validRowCount} valid</span>
            <span className="rounded px-2 py-1 border border-red-800 text-red-400">{summary.errorRowCount} errors</span>
            <span className="rounded px-2 py-1 border border-yellow-800 text-yellow-400">{summary.warningRowCount} warnings</span>
          </div>

          {summary.sampleRows.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="text-gray-500 uppercase text-[0.65rem] border-b border-gray-900">
                <tr><th className="text-left py-1">Employee #</th><th className="text-left py-1">Name</th><th className="text-left py-1">Hire date</th><th className="text-right py-1">Gross</th><th className="text-left py-1 pl-3">Currency</th></tr>
              </thead>
              <tbody>
                {summary.sampleRows.map((r) => (
                  <tr key={r.rowNumber} className="border-b border-gray-900/60">
                    <td className="py-1 text-gray-300">{r.employee_number}</td>
                    <td className="py-1 text-white">
                      {r.name}
                      {r.warning ? <span className="ml-2 text-[0.6rem] rounded px-1 py-0.5 border border-yellow-800 text-yellow-400">warning</span> : null}
                    </td>
                    <td className="py-1 text-gray-400">{r.hire_date}</td>
                    <td className="py-1 text-right font-mono text-xs text-gray-300">{r.gross_salary}</td>
                    <td className="py-1 pl-3 text-gray-400">{r.salary_currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          {summary.errorRowCount > 0 ? (
            <div className="rounded border border-red-900/60 bg-red-950/20 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-300">{summary.errorRowCount} row(s) with errors</span>
                <button type="button" onClick={() => void downloadErrorReport(summary.id)} className="text-xs text-gray-300 border border-gray-700 rounded px-2 py-1 hover:bg-gray-900">
                  Download error report
                </button>
              </div>
              <ul className="max-h-48 overflow-y-auto text-xs text-gray-300 space-y-1">
                {(summary.errors ?? []).map((e, i) => (
                  <li key={i}><span className="text-gray-500">row {e.row}</span> · {e.message}</li>
                ))}
              </ul>
              <label className="flex items-center gap-2 text-xs text-gray-300">
                <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} />
                Import the {summary.validRowCount} valid row(s), skip the {summary.errorRowCount} error row(s).
              </label>
            </div>
          ) : null}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => commit.mutate()}
              disabled={!canCommit || commit.isPending}
              className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
            >
              {commit.isPending ? 'Importing…' : `Import ${summary.validRowCount} employee(s)`}
            </button>
            <button type="button" onClick={() => cancel.mutate()} disabled={cancel.isPending} className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
